import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { Color, theme } from '../../../../constants'
import { Checkbox, Div, Flex, FormattedText } from '../../../../elements'
import { Row } from '../components/Row'
import { WeekTitle } from '../components/WeekTitle'
import { HEADER_HEIGHT, composableZIndex } from '../consts'
import { useSchedule, useSelectedDays } from '../useSchedule'

type HeaderDesign = 'normal' | 'holiday'

type HeaderColors = {
  text: string
  highlight: string
  highlightText?: string
}

const HEADER_DESIGNS: Record<HeaderDesign, HeaderColors> = {
  normal: {
    text: Color.DARK_GREY,
    highlight: Color.BLUE,
    highlightText: Color.WHITE,
  },
  holiday: {
    text: Color.RED,
    highlight: Color.RED,
    highlightText: Color.WHITE,
  },
}

const Head = styled.thead<{ sticky?: boolean }>({
  userSelect: 'none',
  position: 'sticky',
  top: 0,
  zIndex: composableZIndex.head,
  height: HEADER_HEIGHT,
})

const HeadCell = styled.th<{
  isTitle?: boolean
  isScrolled?: boolean
  isSelectable?: boolean
  isSelected?: boolean
  isMonthZoom?: boolean
  design: HeaderDesign
}>(
  ({ isTitle, isScrolled, isSelectable, isSelected, isMonthZoom, design }) => ({
    backgroundColor: Color.WHITE,
    color: HEADER_DESIGNS[design].text,
    padding: 0,
    textAlign: 'center',
    fontWeight: theme.font.weight.regular,

    borderRight: isScrolled ? `1px solid ${Color.GREY}` : undefined,
    position: isTitle ? 'sticky' : 'relative',
    left: isTitle ? 0 : 'auto',
    zIndex: isTitle
      ? composableZIndex.headCellTitle
      : composableZIndex.headCell,

    '.checkbox': {
      visibility: isSelected ? 'visible' : 'hidden',
      position: isMonthZoom ? 'absolute' : 'relative',
    },

    ':hover': isSelectable
      ? {
          '.checkbox': {
            visibility: 'visible',
          },
          span: isMonthZoom
            ? {
                visibility: 'hidden',
              }
            : undefined,
        }
      : undefined,

    span: isMonthZoom && isSelected ? { visibility: 'hidden' } : undefined,
  }),
)

const TitleHighlight = styled.span<{ design: HeaderDesign }>(({ design }) => ({
  padding: theme.spacing[1],
  paddingLeft: theme.spacing[2],
  paddingRight: theme.spacing[2],
  borderRadius: theme.border.radius.rounded,
  backgroundColor: HEADER_DESIGNS[design].highlight,
  color: HEADER_DESIGNS[design].highlightText,
}))

const HeadBorder = styled.th({
  height: 1,
  padding: 0,
  backgroundColor: Color.MEDIUM_GREY,
})

type HeaderProps = {
  title: ReactNode | undefined
}

export const Header = (props: HeaderProps) => {
  const { columnCount, zoom, days, currentWeek, isScrolled } = useSchedule()
  const { isDaySelected, toggleSelected, enableSelectDays } = useSelectedDays()
  const isMonthZoom = zoom === 'month'

  return (
    <>
      <colgroup>
        <col />
        {days.map((column) => (
          <col key={column.dateISO} />
        ))}
      </colgroup>
      <Head>
        <Row>
          <HeadCell isTitle isScrolled={isScrolled} design="normal">
            {props.title ??
              (currentWeek ? <WeekTitle week={currentWeek.number} /> : null)}
          </HeadCell>
          {days.map((column) => {
            const isSelected = isDaySelected(column.dateISO)
            const columnTitle = isMonthZoom ? column.dateShort : column.dateLong
            const holiday = column.holiday
            const isSunday = column.date.isoWeekday() === 7
            const design = holiday?.isPublic || isSunday ? 'holiday' : 'normal'

            return (
              <HeadCell
                key={column.dateISO}
                isSelectable={enableSelectDays}
                isSelected={isSelected}
                isMonthZoom={isMonthZoom}
                design={design}
              >
                <Flex
                  gap={1}
                  items="center"
                  styles={{ justifyContent: 'center', position: 'relative' }}
                >
                  {column.isToday ? (
                    <TitleHighlight design={design}>
                      <FormattedText>{columnTitle}</FormattedText>
                    </TitleHighlight>
                  ) : (
                    <span>
                      <FormattedText>{columnTitle}</FormattedText>
                    </span>
                  )}
                  {enableSelectDays && (
                    <Div className="checkbox">
                      <Checkbox
                        size="sm"
                        checked={isSelected}
                        onChange={() => toggleSelected(column.dateISO)}
                      />
                    </Div>
                  )}
                </Flex>
              </HeadCell>
            )
          })}
        </Row>
        <tr>
          <HeadBorder colSpan={columnCount} />
        </tr>
      </Head>
    </>
  )
}
